/**
 * Mappers to convert API entity shapes (with JSONB `payload`) into
 * the display-ready shapes consumed by index/detail pages.
 *
 * Used on deployed (non-localhost) environments so pages pull live DB data
 * instead of static JSON data files.
 */
import type { Event, Article, Regulation } from "@/src/services/api";
import type { EventListItem, EventData } from "@/types/events";
import { assetUrl } from "./asset";

// ── helpers ──────────────────────────────────────────────────────────────────
const str = (v: unknown): v is string =>
    typeof v === "string" && v.trim().length > 0;

const safePrice = (v: unknown): number => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
};

const safeCurrency = (v: unknown): string => (str(v) ? v : "USD");

export const isLocalDev =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");

// ── Events ───────────────────────────────────────────────────────────────────

/** Convert an API Event entity to the lightweight EventListItem used by the index page. */
export function apiEventToListItem(evt: Event): EventListItem | null {
    const p: any = evt.payload || {};
    // Prefer the backend uuid: older events were persisted with a hybrid
    // `${slug}-${Date.now()}` payload.id that is neither a uuid nor a real
    // slug, which causes detail-page lookups to 404.
    const id = evt.id || evt.slug || p.id;
    const slug = evt.slug || p.slug;
    const header = p.header || evt.title;
    if (!str(id) || !str(slug) || !str(header)) return null;

    return {
        id,
        slug,
        header,
        subheader: str(p.subheader) ? p.subheader : "",
        description: str(p.description) ? p.description : evt.description || "",
        image: assetUrl(p.image || ""),
        date: str(p.date) ? p.date : evt.createdAt,
        price: safePrice(p.price),
        currency: safeCurrency(p.currency),
        category: str(p.category) ? p.category : "Uncategorized",
        location: p.location ? { city: p.location.city || "" } : null,
        organizer: {
            name: str(p.organizer?.name) ? p.organizer.name : "Unknown",
        },
        organization: p.organization
            ? {
                  name: p.organization.name || "",
                  logo: assetUrl(p.organization.logo || ""),
              }
            : undefined,
        isVirtual: Boolean(p.isVirtual),
        isHybrid: Boolean(p.isHybrid),
        tags: Array.isArray(p.tags) ? p.tags : [],
        language: str(p.language) ? p.language : "English",
    };
}

/** Convert an API Event entity to the full EventData used by the detail page. */
export function apiEventToData(evt: Event): EventData | null {
    const p: any = evt.payload || {};
    if (!evt.id && !evt.slug && !p.id) return null;
    return {
        // Prefer backend uuid; legacy payload.id is a `slug-timestamp` hybrid
        // that won't resolve through `/events/:id`.
        id: evt.id || evt.slug || p.id,
        slug: evt.slug || p.slug,
        image: assetUrl(p.image || ""),
        header: p.header || evt.title || "",
        subheader: p.subheader || "",
        description: p.description || evt.description || "",
        fullDescription: p.fullDescription || "",
        ctaText: p.ctaText || "Register",
        price: safePrice(p.price),
        currency: safeCurrency(p.currency),
        date: p.date || evt.createdAt,
        endDate: p.endDate,
        startTime: p.startTime,
        endTime: p.endTime,
        time: p.time,
        timezone: str(p.timezone) ? p.timezone : undefined,
        location: p.location || null,
        organizer: p.organizer || { name: "Unknown", contact: "" },
        organization: p.organization
            ? { ...p.organization, logo: assetUrl(p.organization.logo || "") }
            : undefined,
        capacity: p.capacity,
        language: str(p.language) ? p.language : "English",
        category: str(p.category) ? p.category : "Uncategorized",
        tags: Array.isArray(p.tags) ? p.tags : [],
        speakers: Array.isArray(p.speakers)
            ? p.speakers.map((s: any) => ({
                  ...s,
                  image: assetUrl(s.image || ""),
              }))
            : undefined,
        agenda: p.agenda,
        purchaseURL: p.purchaseURL || "",
        isVirtual: Boolean(p.isVirtual),
        isHybrid: Boolean(p.isHybrid),
        virtualLink: p.virtualLink,
        onDemandStream: Boolean(p.onDemandStream),
        onDemandLink: p.onDemandLink || undefined,
        agendaImage: p.agendaImage ? assetUrl(p.agendaImage) : undefined,
        requirements: p.requirements,
        benefits: p.benefits,
        bannerImages: Array.isArray(p.bannerImages)
            ? p.bannerImages.map((img: string) => assetUrl(img))
            : undefined,
        organizationId: evt.organizationId ?? null,
    };
}

// ── Articles ─────────────────────────────────────────────────────────────────

/** Shape expected by the articles index page. */
export interface ArticleListCard {
    id: string;
    slug?: string;
    approvedId?: string;
    useAdminPreview: boolean;
    title: string;
    shortTitle?: string;
    tags: string[];
    publishedAt: number;
    author: string;
    executiveSummary?: string;
    content?: string;
    isNew: boolean;
}

export function apiArticleToCard(art: Article): ArticleListCard {
    const p: any = art.payload || {};
    const section = Array.isArray(p.sections) ? p.sections[0] : null;
    const summaryContent =
        typeof section?.content === "string" ? section.content : "";
    return {
        id: p.id || art.slug || art.id,
        slug: p.id || art.slug,
        approvedId: art.id,
        useAdminPreview: false,
        title: p.title || art.title || "Untitled",
        shortTitle: p.shortTitle || art.shortTitle || undefined,
        tags: [
            ...(Array.isArray(p.regions) ? p.regions : []),
            ...(Array.isArray(p.tags) ? p.tags : []),
        ],
        publishedAt: art.approvedAt
            ? Date.parse(art.approvedAt)
            : Date.parse(art.createdAt),
        author: str(art.authorName)
            ? art.authorName
            : str(p.author?.name)
              ? p.author.name
              : "Unknown",
        executiveSummary: p.executiveSummary,
        content: summaryContent || undefined,
        isNew: false,
    };
}

// ── Regulations ──────────────────────────────────────────────────────────────

/** Shape expected by the regulations index page (PublishedCard). */
export interface RegulationListCard {
    id: string;
    title: string;
    shortTitle?: string;
    priority?: string;
    tags: string[];
    regions?: string[];
    regionTags?: string[];
    filterTags?: string[];
    miscTags?: string[];
    sortTags?: string[];
    regulationType: "REGULATORY BRIEF" | "ARTICLE";
    publishedAt?: number;
    briefBy?: string;
    executiveSummary?: string;
    content?: string;
    isNew: boolean;
    approvalTimestamp?: number;
    approvedId?: string;
    useAdminPreview: boolean;
    lastUpdated?: string;
}

export function apiRegulationToCard(reg: Regulation): RegulationListCard {
    const p: any = reg.payload || {};
    const hasUpdatesHistory = Array.isArray(p.updates) && p.updates.length > 0;
    const hasBeenUpdated = hasUpdatesHistory;
    const latestUpdateTimestamp = hasUpdatesHistory
        ? p.updates[p.updates.length - 1].timestamp
        : undefined;
    return {
        id: reg.slug || p.slug || reg.id,
        approvedId: reg.id,
        useAdminPreview: false,
        title: p.title || reg.title || "Untitled",
        shortTitle: p.shortTitle || reg.shortTitle || undefined,
        priority: p.priority,
        tags: [
            ...(Array.isArray(p.regionTags) ? p.regionTags : []),
            ...(Array.isArray(p.filterTags) ? p.filterTags : []),
            ...(Array.isArray(p.miscTags) ? p.miscTags : []),
        ],
        regions: p.regions || p.regionTags || [],
        regionTags: p.regionTags || p.regions || [],
        filterTags: p.filterTags || [],
        miscTags: p.miscTags || [],
        sortTags: [...(p.filterTags || [])],
        regulationType: p.type === "article" ? "ARTICLE" : "REGULATORY BRIEF",
        publishedAt: reg.approvedAt
            ? Date.parse(reg.approvedAt)
            : Date.parse(reg.createdAt),
        briefBy: str(reg.authorName)
            ? reg.authorName
            : str(p.author?.name)
              ? p.author.name
              : "Unknown",
        executiveSummary: p.executiveSummary,
        content: typeof p.content === "string" ? p.content : undefined,
        isNew: !hasBeenUpdated,
        lastUpdated:
            latestUpdateTimestamp ||
            (hasBeenUpdated ? reg.updatedAt : undefined),
    };
}
